import { ClientSideSuspense, useUpdateMyPresence } from "@liveblocks/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { TEMP_ELEMENT_DRAG_STORYBOARD_ID } from "@/components/SimpleCanvas/SimpleCanvas";
import { PublishComponentModal } from "@/features/publishComponent/PublishComponentModal";
import {
  useAutoLayoutShortcuts,
  useCanvasSelectionShortcuts,
  useDuplicateShortcut,
  useFillStrokeShortcuts,
  useTextTypographyShortcuts,
  useTypographyShortcuts,
} from "@/hooks/keyboard-shortcuts";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import { useCreateAndUpdateStoryboards } from "@/hooks/useCreateAndUpdateStoryboards";
import { CursorType } from "@/store/globalSettingsSlice";
import {
  setAllSelectedElementKeys,
  setHoveredElementKey,
  setSelectedElementKey,
  setShowContextMenu,
} from "@/store/iframeSlice";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCreateComponentModalStore } from "@/store/zustand/createComponentModalStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { TempoElement } from "@/utils/tempoElement";
import type { Storyboard as StoryboardType } from "@/utils/types";
import {
  useActiveCanvas,
  useActiveMainCanvas,
  useAutoUpdateRef,
  useCanvasId,
  useInitialRenderCallback,
} from "@/utils/utils";
import { DependencyConfirmationModalWrapper } from "../DependencyConfirmationModal";
import DesignToolsPicker from "../DesignToolsPicker";
import { FileConflictModalWrapper } from "../FileConflictModal";
import LiveblocksCursors from "../LiveblocksCursors";
import { getUpdateClassChangeForElement } from "../RightStyle/TailwindStylingPanel/utils/updateElementUtils";
import SimpleCanvas, {
  type CanvasItemRef,
  type CanvasItemState,
  type SimpleCanvasRef,
} from "../SimpleCanvas/SimpleCanvas";
import Annotations from "./Annoations";
import CanvasContextMenu from "./CanvasContextMenu";
import CanvasLoadingOverlay from "./CanvasLoadingOverlay";
import { CanvasSideEffect } from "./CanvasSideEffect";
import { PagesSideEffects } from "../../store/zustand/pagesStore";
import { CreateComponentModal } from "./CreateComponentModal";
import { calculateDropPosition } from "./hooks/useCalculateDropPosition";
import { useCanvasContextMenu } from "./hooks/useCanvasContextMenu";
import { useCanvasViewportControl } from "./hooks/useCanvasViewportControl";
import {
  CopyPasteHandler,
  useGetCopyDataForSelectedElements,
} from "./hooks/useCopyPaste";
import { useFullScreenMode } from "./hooks/useFullScreenMode";
import { useOnKeyDown, useOnKeyUp } from "./hooks/useKeyEvents";
import { useMaybeInitializeMainStoryboard } from "./hooks/useMaybeInitializeMainStoryboard";
import { useMaybeInitializeSubcanvasStoryboards } from "./hooks/useMaybeInitializeSubcanvasStoryboards";
import { useOnDrop } from "./hooks/useOnDrop";
import { DriveModeIframeOverlay } from "./IframePool/DriveModeIframeOverlay";
import { ExitFullScreenOverlay } from "./IframePool/ExitFullScreenOverlay";
import { FullscreenIframe } from "./IframePool/FullscreenIframe";
import { IframePool } from "./IframePool/IframePool";
import TutorialOverlay from "./IframePool/TutorialOverlay";
import { useApplyInstantUpdatesForCanvas } from "./Storyboard/StaticIframe/hooks/useApplyInstantUpdates";
import { useAllStoryboardsWithErrorOverlays } from "./Storyboard/StaticIframe/hooks/useStoryboardState";
import { Storyboard, type StoryboardRef } from "./Storyboard/StoryboardV2";
import { StoryGenerationUI } from "./StoryGenerationManager";
import SubcanvasHeader from "./SubcanvasHeader";
import { useIframeStore } from "./stores/iframeStore";
import { usePagesStore } from "@/store/zustand/pagesStore";
import {
  DEFAULT_STORYBOARD_TEXT_HEIGHT,
  DEFAULT_STORYBOARD_TEXT_WIDTH,
} from "./utils";
import type { ClipboardData } from "./utils/canvasClipboardData";
import ZoomControlsV2 from "./ZoomControlsV2";

const Canvas = () => {
  // ================================================================================
  // State hooks and refs
  // ================================================================================
  const dispatch = useDispatch();

  // Canvas & Storyboard state
  const canvasData = useActiveCanvas();
  const mainCanvas = useActiveMainCanvas(); // The main canvas you'd see in the URL, never a subcanvas

  // Pages state
  const canvasId = useCanvasId();
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );
  const pageStoryboards = usePagesStore(
    (state) =>
      state.pages[canvasId]?.find((p) => p.id === selectedPageId)?.storyboards,
  );

  // The selected page is only valid to show on the canvas once it has initial storyboards
  const pageKey = (pageStoryboards && selectedPageId) || undefined;

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey,
  );
  const temporaryStoryboard = useCanvasStore(
    (store) => store.temporaryStoryboard,
  );
  const isComponentSubCanvas = Boolean(canvasComponentKey);

  const { selectedElement, storyboardIdSelected } = useSelectedNavTreeNode();
  const { multiselectedElements, multiselectedStoryboardIds } =
    useMultiselectedNavTreeNode();
  const { hoveredElement, storyboardIdHovered } = useHoveredNavTreeNode();

  const selectedStoryboard = useMemo(() => {
    if (!storyboardIdSelected) {
      return null;
    }

    return mainCanvas?.storyboards.find(
      (storyboard) => storyboard.id === storyboardIdSelected,
    );
  }, [mainCanvas, storyboardIdSelected]);

  const canvasDataRef = useAutoUpdateRef(canvasData);

  const hoveredElementRef = useAutoUpdateRef(hoveredElement);

  const updateMyPresence = useUpdateMyPresence();

  // Story generation dependencies
  const { componentKeyToComponent } = useAssetComponents();
  // @ts-expect-error TS2538
  const canvasComponent = componentKeyToComponent?.[canvasComponentKey];

  const menuLeft = useCanvasStore((state) => state.menuLeft);
  const menuTop = useCanvasStore((state) => state.menuTop);
  const showContextMenu = useSelector(
    (state: any) => state.iframe.showContextMenu,
  );

  const isSpacePressed = useSelector(
    (state: any) => state.globalSettings.isSpacePressed,
  );

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType,
  );

  const simpleCanvasRef = useRef<SimpleCanvasRef>(null);
  const { isFullScreenEnabled, exitFullscreen } = useFullScreenMode();

  const isFtueGenerating = useProjectCreateStore(
    (state) => state.isFtueGenerating,
  );

  const iframesRef = useRef<
    Record<
      string,
      {
        ref: StoryboardRef;
        width: number;
        height: number;
      }
    >
  >({});
  const cursorTypeRef = useRef(cursorType);
  const canvasIdRef = useRef<string | null>(null);

  useEffect(() => {
    cursorTypeRef.current = cursorType;
  }, [cursorType]);

  useEffect(() => {
    if (canvasData?.id) {
      canvasIdRef.current = canvasData.id;
    }
  }, [canvasData?.id]);

  const isMetaPressed = useSelector(
    (state: any) => state.globalSettings.isMetaPressed,
  );
  const isCtrlPressed = useSelector(
    (state: any) => state.globalSettings.isCtrlPressed,
  );

  const draggingComponent = useSelector(
    (state: any) => state.iframe.draggingComponent,
  );

  const allStoryboardsWithErrors = useAllStoryboardsWithErrorOverlays();

  const { handleDropEvent, insertClipboardDataIntoCanvas } = useOnDrop();
  const insertClipboardDataIntoCanvasRef = useAutoUpdateRef(
    insertClipboardDataIntoCanvas,
  );

  useApplyInstantUpdatesForCanvas();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );
  const selectedTreeElement = selectedElement?.isKnownElement()
    ? treeElementLookup?.[selectedElement.codebaseId]
    : null;

  const isCreateComponentModalOpen = useCreateComponentModalStore(
    (state) => state.isOpen,
  );
  const elementData = useCreateComponentModalStore(
    (state) => state.elementData,
  );
  const closeModal = useCreateComponentModalStore((state) => state.closeModal);

  // ================================================================================
  // Keyboard and Cursor logic
  // ================================================================================

  const cursorStyles = {
    draw: "crosshair",
    default: "auto",
    text: "text",
    edit: "text",
    pan: "grab",
  };

  const activeCursorType = isSpacePressed ? CursorType.Pan : cursorType;
  const cursorStyle = cursorStyles[activeCursorType] || cursorStyles.default;

  // Keyboard event handlers
  const { onKeyDown, handleTypographyAdjustment } = useOnKeyDown(iframesRef);
  const onKeyUp = useOnKeyUp();

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [onKeyDown, onKeyUp]);

  // ================================================================================
  // Storyboard logic
  // ================================================================================

  // Initialize main storyboard if needed
  useMaybeInitializeMainStoryboard();

  // Initialize subcanvas storyboards if needed
  useMaybeInitializeSubcanvasStoryboards();

  const getCopyDataForSelectedElements = useGetCopyDataForSelectedElements();
  const getCopyDataForSelectedElementsRef = useAutoUpdateRef(
    getCopyDataForSelectedElements,
  );

  const storyboardsToRender = useMemo(() => {
    const extraStoryboards: (StoryboardType & { hidden?: boolean })[] = [];
    if (temporaryStoryboard) {
      extraStoryboards.push(temporaryStoryboard);
    }
    const mainStoryboards = canvasComponentKey
      ? canvasData?.storyboards
      : pageStoryboards;
    return (mainStoryboards || []).concat(extraStoryboards);
  }, [canvasData, pageStoryboards, temporaryStoryboard, selectedPageId]);

  // Calculate max Z-index for story generation UI positioning
  const maxZIndex = useMemo(() => {
    return (
      storyboardsToRender
        ?.map((storyboard: any) => storyboard.zIndex)
        ?.reduce((a: any, b: any) => Math.max(a, b), 0) || 0
    );
  }, [storyboardsToRender]);

  const renderStoryboard = useCallback(
    (
      storyboard: StoryboardType,
      canvasItemState: CanvasItemState,
      registerRef: (ref: CanvasItemRef | null) => void,
    ) => {
      return (
        <Storyboard
          ref={(ref: StoryboardRef | null) => {
            if (ref) {
              iframesRef.current[storyboard.id] = {
                ref,
                width: storyboard.width,
                height: storyboard.height,
              };
            } else {
              delete iframesRef.current[storyboard.id];
            }
          }}
          storyboard={storyboard}
          canvasItemState={canvasItemState}
          simpleCanvasRef={simpleCanvasRef}
          registerRef={registerRef}
        />
      );
    },
    [],
  );

  // ================================================================================
  // Context menu placement
  // ================================================================================

  const { callContextMenu } = useCanvasContextMenu();

  // ================================================================================
  // CanvasV2 controlling SimpleCanvas
  // ================================================================================

  // Storyboard management hooks
  const { updateStoryboards, startDrawStoryboard, finalizeDrawStoryboard } =
    useCreateAndUpdateStoryboards();

  // Register keyboard shortcuts
  useCanvasSelectionShortcuts();
  useDuplicateShortcut();
  useAutoLayoutShortcuts();
  useFillStrokeShortcuts();
  useTypographyShortcuts({
    onTypographyAdjustment: handleTypographyAdjustment,
  });
  useTextTypographyShortcuts();
  useCanvasViewportControl({ simpleCanvasRef, storyboardsToRender });

  const pageViewport = usePagesStore(
    (state) => state.pageViewport[canvasData?.id || ""],
  );
  const initialViewport = useMemo(() => {
    if (!selectedPageId || !pageViewport) return undefined;
    return pageViewport[selectedPageId];
  }, [pageViewport, selectedPageId]);

  useEffect(() => {
    if (!selectedElement?.isEmpty()) {
      // Focused just determines what is interactable
      simpleCanvasRef.current?.setFocusedId(selectedElement.storyboardId);

      // Selection determines what is highlighted
      // Only select if it's the storyboard itself, not an element inside it
      if (storyboardIdSelected) {
        const allSelectedStoryboardIds = multiselectedStoryboardIds?.length
          ? multiselectedStoryboardIds
          : [storyboardIdSelected];
        simpleCanvasRef.current?.setSelectedIds(
          new Set(allSelectedStoryboardIds),
        );
      } else {
        // No selected ids -> element inside the storyboard
        simpleCanvasRef.current?.setSelectedIds(new Set());
      }
    } else {
      simpleCanvasRef.current?.setFocusedId(null);
      simpleCanvasRef.current?.setSelectedIds(new Set());
    }
  }, [
    selectedElement,
    multiselectedElements,
    storyboardsToRender,
    storyboardIdSelected,
    multiselectedStoryboardIds,
  ]);

  // ================================================================================
  // CanvasV2 reacting to events in SimpleCanvas
  // ================================================================================

  const onZoom = useCallback((zoom: number) => {
    if (canvasIdRef.current) {
      useCanvasStore
        .getState()
        .setCanvasPosValue(canvasIdRef.current, { zoomPerc: zoom });
    }
  }, []);

  const onMouseMove = useCallback(
    ({
      clientX,
      clientY,
      canvasX,
      canvasY,
      draggingItemId,
    }: {
      clientX: number;
      clientY: number;
      canvasX: number;
      canvasY: number;
      draggingItemId?: string;
    }) => {
      updateMyPresence({
        cursor: {
          x: Math.round(canvasX),
          y: Math.round(canvasY),
        },
        selectedPageId,
      });
      useCanvasStore.getState().setCanvasMousePos(canvasX, canvasY);

      // Fallback as sometimes the mouse leave events don't trigger
      if (
        !hoveredElementRef.current?.isEmpty() &&
        simpleCanvasRef.current?.isCanvasAtClientCoordinates(clientX, clientY)
      ) {
        const { items: itemsAtCoordinates } =
          simpleCanvasRef.current?.getItemsAtCoordinates(
            canvasX,
            canvasY,
            draggingItemId ? [draggingItemId] : undefined,
            true,
          ) ?? {};
        if (!itemsAtCoordinates?.length) {
          dispatch(setHoveredElementKey(null));
        }
      }
    },
    [selectedPageId, updateMyPresence, hoveredElementRef, dispatch],
  );

  const onTransformChange = useCallback(
    (transform: { x: number; y: number; zoom: number }, pageKey?: string) => {
      if (canvasData?.id) {
        // Update canvasStore for real-time display
        useCanvasStore.getState().setCanvasPosValue(canvasData.id, {
          transformX: transform.x,
          transformY: transform.y,
          zoomPerc: transform.zoom,
        });

        // Directly update pagesStore for the correct page
        if (pageKey) {
          usePagesStore.getState().setPageViewport(canvasData.id, pageKey, {
            x: transform.x,
            y: transform.y,
            zoom: transform.zoom,
          });
        }
      }
    },
    [canvasData?.id],
  );

  const onDrawRectStart = useCallback(
    (pos: { x: number; y: number }) => {
      if (cursorTypeRef.current === CursorType.Draw) {
        const newStoryboard: any = startDrawStoryboard(pos.x, pos.y);

        return newStoryboard;
      } else if (cursorTypeRef.current === CursorType.Text) {
        // Just immediately create a text storyboard
        const textStoryboard = startDrawStoryboard(pos.x, pos.y);
        textStoryboard.width = DEFAULT_STORYBOARD_TEXT_WIDTH;
        textStoryboard.height = DEFAULT_STORYBOARD_TEXT_HEIGHT;
        finalizeDrawStoryboard(textStoryboard);
        return true; // Handled, don't start any kind of draw select
      }
      return false;
    },
    [cursorTypeRef, startDrawStoryboard, finalizeDrawStoryboard],
  );

  const onDrawRectEnd = useCallback(
    async (item: StoryboardType) => {
      await finalizeDrawStoryboard(item);
    },
    [finalizeDrawStoryboard],
  );

  const onSelectionChange = useInitialRenderCallback(
    (selectedIds: Set<string>) => {
      const firstSelectedId = Array.from(selectedIds).pop();
      if (selectedIds.size === 0) {
        dispatch(
          setAllSelectedElementKeys({
            multiSelectedElementKeys: [],
            selectedElementKey: null,
          }),
        );
        return;
      }

      if (selectedIds.size === 1) {
        dispatch(
          setSelectedElementKey(
            TempoElement.forStoryboard(firstSelectedId as string).getKey(),
          ),
        );
      } else {
        dispatch(
          setAllSelectedElementKeys({
            multiSelectedElementKeys: Array.from(selectedIds).map((id) =>
              TempoElement.forStoryboard(id as string).getKey(),
            ),
            selectedElementKey: TempoElement.forStoryboard(
              firstSelectedId as string,
            ).getKey(),
          }),
        );
      }
    },
  );

  const onMouseEnter = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>, itemId: string) => {
      if (itemId === TEMP_ELEMENT_DRAG_STORYBOARD_ID) {
        return;
      }

      if (
        e.metaKey ||
        e.ctrlKey ||
        isSpacePressed ||
        allStoryboardsWithErrors.has(itemId) ||
        draggingComponent ||
        cursorType !== CursorType.Pointer
      ) {
        return;
      }

      dispatch(
        setHoveredElementKey(TempoElement.forStoryboard(itemId).getKey()),
      );
    },
    [
      allStoryboardsWithErrors,
      cursorType,
      dispatch,
      draggingComponent,
      isSpacePressed,
    ],
  );

  const onMouseLeave = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>, itemId: string) => {
      if (
        e.metaKey ||
        e.ctrlKey ||
        isSpacePressed ||
        allStoryboardsWithErrors.has(itemId) ||
        draggingComponent ||
        cursorType !== CursorType.Pointer ||
        simpleCanvasRef.current?.getIsDragging()
      ) {
        return;
      }

      dispatch(setHoveredElementKey(null));
    },
    [
      allStoryboardsWithErrors,
      cursorType,
      dispatch,
      draggingComponent,
      isSpacePressed,
    ],
  );

  const onCanvasMouseUp = useInitialRenderCallback(
    (e: React.MouseEvent<Element, MouseEvent> | MouseEvent) => {
      if (e.button === 2) {
        return;
      }
      dispatch(setShowContextMenu(false));
    },
  );

  const onCancelDragItem = useInitialRenderCallback(() => {
    // Notify all iframes that drag has finished
    useIframeStore.getState().setDragFinished();

    useCanvasStore.getState().setTemporaryStoryboardForDrag(null);
  });

  const onDropItemIntoOtherItem = useInitialRenderCallback(
    async (
      itemId: string,
      itemDroppingIntoId: string,
      dropOffsetX: number,
      dropOffsetY: number,
    ) => {
      // Notify all iframes that drag has finished
      useIframeStore.getState().setDragFinished();

      // Clear the temporary storyboard after fetching it
      const temporaryStoryboard = useCanvasStore.getState().temporaryStoryboard;
      const isDraggingAbsolute = useCanvasStore.getState().isDraggingAbsolute;
      const draggingPosition = useCanvasStore.getState().draggingPosition;
      useCanvasStore.getState().setTemporaryStoryboardForDrag(null);

      if (!canvasDataRef.current) return;

      // Check if this is a temporary storyboard first
      let storyboard: StoryboardType | undefined;
      if (itemId === TEMP_ELEMENT_DRAG_STORYBOARD_ID && temporaryStoryboard) {
        storyboard = temporaryStoryboard;
      } else {
        storyboard = canvasDataRef.current?.storyboards.find(
          (sb) => sb.id === itemId,
        );
      }

      if (
        !hoveredElementRef.current ||
        hoveredElementRef.current?.storyboardId === itemId ||
        !storyboard
      ) {
        return;
      }

      // Check if this is an element drag (temporary storyboard created from element)
      const isDragOutOfStoryboard =
        storyboard?.id === TEMP_ELEMENT_DRAG_STORYBOARD_ID;

      let copyData: ClipboardData | undefined;
      if (isDragOutOfStoryboard) {
        // Use the element drag data stored in the temporary storyboard
        copyData = storyboard._elementDragData;
        if (!copyData) {
          return;
        }

        const droppedElement = copyData.selectedElementKey
          ? TempoElement.fromKey(copyData.selectedElementKey)
          : undefined;

        // Was absolute and dropped in the same storyboard - move using tailwind css
        if (
          isDraggingAbsolute &&
          hoveredElementRef.current.storyboardId ===
            droppedElement?.storyboardId
        ) {
          let dropCoordinates =
            draggingPosition === "absolute"
              ? useCanvasStore.getState().dropCoordinatesForAbsolute
              : useCanvasStore.getState().dropCoordinatesInSb;

          // If dropCoordinates are null (due to fast dropping), calculate them directly
          if (!dropCoordinates) {
            const hoveredNavNode =
              useCanvasStore.getState().elementKeyToNavNodeLookup[
                hoveredElementRef.current.getKey()
              ];
            const hoveredStoryboardId = hoveredElementRef.current.storyboardId;
            const iframeData = iframesRef.current[hoveredStoryboardId];

            if (hoveredNavNode && iframeData?.ref) {
              // Get the iframe document
              const iframeDoc = iframeData.ref.getIframeDocument();

              if (iframeDoc) {
                const { dropCoordinatesInSb, dropCoordinatesForAbsolute } =
                  calculateDropPosition(
                    dropOffsetX,
                    dropOffsetY,
                    hoveredNavNode,
                    iframeDoc,
                    draggingPosition === "absolute",
                  );

                dropCoordinates =
                  draggingPosition === "absolute"
                    ? dropCoordinatesForAbsolute || null
                    : dropCoordinatesInSb || null;
              }
            }

            // If still no coordinates, return
            if (!dropCoordinates) {
              return;
            }
          }

          // Just move using tailwind css, no need to insert or move
          const updateClassesChange = getUpdateClassChangeForElement({
            codebaseId: droppedElement?.codebaseId ?? "",
            newLeft: Math.round(
              dropCoordinates.x - (storyboard._elementDragOffsetX || 0),
            ),
            newTop: Math.round(
              dropCoordinates.y - (storyboard._elementDragOffsetY || 0),
            ),
          });
          updateClassesChange?.setSelectedElementsAfterInstantUpdate(
            copyData.selectedElementKey!,
            [],
          );

          if (!updateClassesChange) {
            return;
          }

          useDesignCommandManager
            .getState()
            .executeCommand(updateClassesChange);
          return;
        }

        copyData.insertIntoHoveredElement = true;
        copyData.deleteSourceElements = true;

        // Was not absolute and dropped into a child - no change
        if (
          droppedElement &&
          droppedElement.storyboardId ===
            hoveredElementRef.current?.storyboardId &&
          hoveredElementRef.current &&
          (droppedElement.isParentOf(hoveredElementRef.current) ||
            hoveredElementRef.current.isEqual(droppedElement))
        ) {
          // Just reset
          return;
        }

        // Dropping into the same parent
        if (
          droppedElement?.storyboardId ===
            hoveredElementRef.current?.storyboardId &&
          hoveredElementRef.current.isDirectParentOf(droppedElement)
        ) {
          const dropPosition = useCanvasStore.getState().dropPosition;
          if (
            storyboard._elementDragIndex !== undefined &&
            dropPosition?.index !== undefined
          ) {
            // Dropping in same position, just reset
            if (dropPosition?.index === storyboard._elementDragIndex) {
              return;
            }

            // Also dropping in the same position after accounting for the source element being removed
            if (
              dropPosition?.index > storyboard._elementDragIndex &&
              dropPosition?.index === storyboard._elementDragIndex + 1
            ) {
              return;
            }
          }
        }
      } else {
        // Regular storyboard drop
        copyData =
          (await getCopyDataForSelectedElementsRef.current()) ?? undefined;

        if (copyData) {
          copyData.insertIntoHoveredElement = true;
          copyData.deleteSourceStoryboard = true;
          copyData.sourceStoryboardName = storyboard?.name;
        }
      }

      if (!copyData) {
        return;
      }

      insertClipboardDataIntoCanvasRef.current(copyData);
    },
  );

  const onDropItemOntoCanvas = useInitialRenderCallback((itemId: string) => {
    // Notify all iframes that drag has finished
    useIframeStore.getState().setDragFinished();

    if (!canvasDataRef.current) return;

    // Only handle temporary storyboard drops (drag out from storyboard)
    if (itemId !== TEMP_ELEMENT_DRAG_STORYBOARD_ID) {
      return;
    }

    // Check if this is a temporary storyboard first
    const temporaryStoryboard = useCanvasStore.getState().temporaryStoryboard;
    if (!temporaryStoryboard) {
      return;
    }

    // Clear the temporary storyboard before creating new storyboard
    useCanvasStore.getState().setTemporaryStoryboardForDrag(null);

    const copyData = temporaryStoryboard._elementDragData;
    if (!copyData) {
      // Clear the temporary storyboard even if no copy data
      return;
    }

    copyData.forceCreateStoryboard = true;
    copyData.deleteSourceElements = true;

    // Use the existing clipboard system to create a new storyboard
    insertClipboardDataIntoCanvasRef.current(copyData);
  });

  useEffect(() => {
    useCanvasStore.getState().setQueryStoryboardContentSize((storyboardId) => {
      const document =
        iframesRef.current[storyboardId]?.ref.getIframeDocument();
      const root = document?.getElementById("root");
      if (!root) {
        return null;
      }

      const LocalHTMLElement = (root.getRootNode() as HTMLDocument).defaultView
        ?.HTMLElement;
      if (!LocalHTMLElement) {
        return null;
      }

      let elToFit: HTMLElement;
      if (root.children.length > 2) {
        elToFit = root;
      } else if (root.children[0] instanceof LocalHTMLElement) {
        elToFit = root.children[0];
      } else {
        return null;
      }
      const oldWidth = elToFit.style.width;
      const oldHeight = elToFit.style.height;
      elToFit.style.width = "fit-content";
      elToFit.style.height = "fit-content";
      const fitRect = elToFit.getBoundingClientRect();
      const fitWidth = fitRect.width;
      const fitHeight = fitRect.height;
      elToFit.style.width = oldWidth;
      elToFit.style.height = oldHeight;

      return { width: fitWidth, height: fitHeight };
    });
  }, []);

  // ================================================================================
  // Render
  // ================================================================================

  return (
    <>
      <DependencyConfirmationModalWrapper />
      <FileConflictModalWrapper />
      <PublishComponentModal />
      <CanvasSideEffect />
      <PagesSideEffects />
      <div
        className={
          "outline-hidden bg-secondary-200 grow p-0 overflow-hidden relative pointer-events-auto"
        }
        style={{ cursor: cursorStyle }}
        onMouseLeave={(e) => {
          updateMyPresence({
            cursor: null,
          });
        }}
      >
        <SimpleCanvas
          ref={simpleCanvasRef}
          items={storyboardsToRender}
          renderItem={renderStoryboard}
          onZoom={onZoom}
          onItemsChange={updateStoryboards}
          onSelectionChange={onSelectionChange}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          forcePanOnClick={cursorType === CursorType.Pan || isSpacePressed}
          onMouseMove={onMouseMove}
          onTransformChange={onTransformChange}
          pageKey={pageKey}
          initialViewport={initialViewport}
          onDrawRectStart={onDrawRectStart}
          onDrawRectEnd={onDrawRectEnd}
          onMouseUp={onCanvasMouseUp}
          onMouseDown={({ item, itemId }) => {
            if (!itemId && !(cursorType === CursorType.Pan || isSpacePressed)) {
              dispatch(setSelectedElementKey(null));
            }
          }}
          onContextMenu={(pos, itemId) => {
            callContextMenu(pos.clientX, pos.clientY, itemId);
          }}
          forceInteractableIfMouseOver={
            isMetaPressed ||
            isCtrlPressed ||
            draggingComponent ||
            cursorType === CursorType.Draw ||
            cursorType === CursorType.Text ||
            cursorType === CursorType.Drive
          }
          forceItemIdsInteractable={allStoryboardsWithErrors}
          preventAllActions={cursorType === CursorType.Drive}
          preventSelect={Boolean(
            cursorType === CursorType.Draw || cursorType === CursorType.Text,
          )}
          onDrop={handleDropEvent}
          canDropDraggingItems={
            !hoveredElement?.isEmpty() &&
            selectedStoryboard?.type !== STORYBOARD_TYPES.APPLICATION
          }
          onDropItemIntoOtherItem={onDropItemIntoOtherItem}
          onDropItemOntoCanvas={onDropItemOntoCanvas}
          onCancelDragItem={onCancelDragItem}
        >
          {/* Variant box for subcanvas */}
          <div className="absolute inset-0" style={{ zIndex: -1 }}>
            <Annotations />
          </div>

          {/* Story Generation UI - needs to be inside canvas transform */}
          <StoryGenerationUI
            storyboardsToDraw={storyboardsToRender}
            canvasComponentKey={canvasComponentKey || undefined}
            maxZIndex={maxZIndex}
            canvasComponent={canvasComponent}
          />

          {/* <div className="absolute inset-0" style={{ zIndex: 1000 }}>
            {DragOutOutlineInstance}
          </div> */}
          <ClientSideSuspense fallback={<></>}>
            <LiveblocksCursors />
          </ClientSideSuspense>

          <IframePool />
        </SimpleCanvas>

        {isFullScreenEnabled &&
          (isFtueGenerating ? <TutorialOverlay /> : <FullscreenIframe />)}

        <div id="styling-panel-portal-root" />

        {/* Context menu for storyboards */}
        <CanvasContextMenu
          showMenu={showContextMenu}
          left={menuLeft || 0}
          top={menuTop || 0}
        />

        {/* Drive Mode Iframe Overlay - handles iframe positioning during drive mode */}
        <DriveModeIframeOverlay />

        {/* Copy/paste handler */}
        <CopyPasteHandler />

        {/* Create Component Modal */}
        <CreateComponentModal
          isOpen={isCreateComponentModalOpen}
          onClose={closeModal}
          elementData={elementData ?? undefined}
        />

        {/* Overlay for zoom controls, design tools, loading progress. */}
        <div
          className="absolute flex flex-col items-center pointer-events-none top-0 left-0 right-0 bottom-0 justify-between"
          style={{ zIndex: isFullScreenEnabled ? 11000 : 2000 }}
        >
          <CanvasLoadingOverlay />
          {isFullScreenEnabled && !isFtueGenerating && (
            <ExitFullScreenOverlay
              onExit={exitFullscreen}
              className="absolute top-0 left-0 right-0 z-50 pointer-events-auto"
            />
          )}
          <div className="w-full flex flex-col items-center">
            {isComponentSubCanvas && <SubcanvasHeader />}
          </div>
          {!isFullScreenEnabled && (
            <div className="w-full flex flex-col items-center">
              <div className="w-full flex flex-row items-end pt-3 relative justify-center pb-6">
                <div className="z-10">
                  <DesignToolsPicker />
                </div>
                <ZoomControlsV2 simpleCanvasRef={simpleCanvasRef} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
Canvas.displayName = "CanvasV2";

export default Canvas;
